import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../utils/api';
import { mapCategoriesToRussian } from '../utils/categories';
import prompthubEllipse from '../images/icons/prompthub-ellipse.svg';

// Тип промпта из API
type ApiPrompt = {
    id: number;
    title: string;
    description: string;
    categories: string[];
    categoriesEn: string[];
    prompt: string;
    promptForExample: string;
    example: string;
    imageName: string;
    videoData: {
        url: string;
    };
    imageData: {
        url: string;
        urlOptimized: string;
        imagename: string;
        groupId: number;
        colors: {
            AverageBorderColor: string;
            ColorWizBack: string;
            ColorWizButton: string;
            ColorWizButtonText: string;
            ColorWizText: string;
            MainColor: string;
            SecondColor: string;
        };
    };
    isLiked: boolean;
    likes: number;
    visits: number;
    author?: {
        uid?: string;
        login: string;
        name: string;
        image: string;
        promptsCount: number;
    };
};

// Тип промпта для frontend
export type Prompt = {
    id: string;
    title: string;
    description: string;
    categories: string[];
    prompt: string;
    prompt_for_example: string;
    example: string;
    imageName: string;
    imageData: {
        urlOptimized: string;
        colors: {
            MainColor: string;
        };
    };
    video: string;
    hex: string;
    likes: number;
    visits: number;
    // Properties expected by PromptCard component
    isEditorial: boolean;
    avatar: string;
    image: string;
    isLiked: boolean;
    tags: string[]; // alias for categories
};

type UsePromptsDataReturn = {
    /** Текущий список промптов */
    prompts: Prompt[];
    /** Состояние загрузки */
    loading: boolean;
    /** Есть ли еще данные для загрузки */
    hasMore: boolean;
    /** Функция для загрузки следующей порции данных */
    loadMore: () => Promise<void>;
    /** Функция для сброса данных */
    reset?: () => void;
};

// Параметры для API запроса
type PromptsApiParams = {
    cursor?: string;
    category?: string;
    limit?: number;
    hash?: string;
};

// Ответ API
type PromptsApiResponse = {
    prompts: ApiPrompt[];
    next_cursor?: string;
    hasMore: boolean;
};

// Счетчики для отслеживания последовательности промптов
let consecutiveEditorialCount = 0;
let consecutiveNonEditorialCount = 0;

// Адаптация данных из API под формат frontend
const adaptApiPromptToFrontend = (apiPrompt: ApiPrompt): Prompt => {
    // Определяем isEditorial с учетом ограничения на последовательность
    let isEditorial: boolean;

    if (apiPrompt.author?.uid !== '0') {
        isEditorial = false;
        consecutiveEditorialCount = 0;
        consecutiveNonEditorialCount = 1;
    } else if (consecutiveEditorialCount >= 2) {
        // Если уже 2 подряд editorial, следующий обязательно не-editorial
        isEditorial = false;
        consecutiveEditorialCount = 0;
        consecutiveNonEditorialCount = 1;
    } else if (consecutiveNonEditorialCount >= 2) {
        // Если уже 2 подряд не-editorial, следующий обязательно editorial
        isEditorial = true;
        consecutiveNonEditorialCount = 0;
        consecutiveEditorialCount = 1;
    } else {
        // Иначе используем случайное распределение (50% вероятность)
        isEditorial = Math.random() < 0.5;

        if (isEditorial) {
            consecutiveNonEditorialCount = 0;
            consecutiveEditorialCount++;
        } else {
            consecutiveEditorialCount = 0;
            consecutiveNonEditorialCount++;
        }
    }

    return {
        ...apiPrompt,
        id: apiPrompt.id.toString(),
        title: apiPrompt.title,
        description: apiPrompt.description,
        categories: apiPrompt.categories,
        prompt: apiPrompt.prompt,
        prompt_for_example: apiPrompt.promptForExample,
        example: apiPrompt.example,
        imageName: apiPrompt.imageName,
        imageData: apiPrompt.imageData,
        hex: apiPrompt.imageData.colors.MainColor,
        likes: apiPrompt.likes,
        visits: apiPrompt.visits,
        video: apiPrompt.videoData.url,
        isEditorial,
        avatar: apiPrompt?.author?.image || prompthubEllipse,
        image: apiPrompt.imageData.urlOptimized,
        isLiked: apiPrompt.isLiked,
        // Берем первые 3 категории и преобразуем в русские названия
        tags: mapCategoriesToRussian(apiPrompt.categoriesEn.slice(0, 3)),
    };
};

// Функция для сброса счетчиков последовательности
const resetEditorialSequence = (): void => {
    consecutiveEditorialCount = 0;
    consecutiveNonEditorialCount = 0;
};

/**
 * Хук для работы с данными промптов с поддержкой бесконечной подгрузки
 * @param pageSize - Количество элементов на одной странице
 * @param categories - Массив категорий для фильтрации
 * @param instanceId - Уникальный идентификатор экземпляра хука для генерации уникального hash
 * @returns Объект с данными и методами для управления промптами
 */
export const usePromptsData = (
    pageSize: number = 20,
    categories?: string[],
    instanceId?: string,
): UsePromptsDataReturn => {
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | undefined>();
    const [hash, setHash] = useState(() => {
        const baseHash = Date.now().toString();

        return instanceId ? `${instanceId}_${baseHash}` : baseHash;
    }); // Уникальный hash для каждого экземпляра
    const [shouldReload, setShouldReload] = useState(false); // Флаг для отслеживания необходимости перезагрузки

    // Refs для debounce механизма
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const previousCategoriesRef = useRef<string[] | undefined>(categories);

    // Функция для загрузки данных с API
    const loadMore = useCallback(async (): Promise<void> => {
        setLoading(true);
        setHasMore(true);

        try {
            // Параметры для API запроса
            const params: PromptsApiParams = {
                limit: pageSize,
                hash,
            };

            if (categories) {
                params.category = categories.join(',');
            }

            // Добавляем cursor для пагинации (кроме первого запроса)
            if (nextCursor) {
                params.cursor = nextCursor;
            }

            // Формируем query string
            const queryString = new URLSearchParams(
                Object.entries(params).filter(([, value]) => value !== undefined) as [string, string][],
            ).toString();

            // Запрос к API для получения промптов
            const response = await apiClient.get<PromptsApiResponse>(`/prompts?${queryString}`);

            if (response.prompts.length === 0) {
                // Имитация более длительной загрузки для лучшего UX
                await new Promise(resolve => setTimeout(resolve, 2000));
                setHasMore(false);

                return;
            }
            // Адаптируем данные API под формат frontend
            const adaptedPrompts = response.prompts.map(adaptApiPromptToFrontend);

            // Добавляем новые данные к существующим с дедупликацией
            setPrompts(prev => {
                const existingIds = new Set(prev.map(prompt => prompt.id));
                const newPrompts = adaptedPrompts.filter(prompt => !existingIds.has(prompt.id));

                return [...prev, ...newPrompts];
            });

            if (!response.next_cursor) {
                setHasMore(false);

                return;
            }
            // Обновляем состояние пагинации
            setNextCursor(response.next_cursor);
        } catch (error) {
            console.error('Error loading prompts:', error);
        } finally {
            setLoading(false);
        }
    }, [nextCursor, pageSize, hash, categories]);

    // Функция для сброса данных
    // const reset = useCallback(() => {
    //     setPrompts([]);
    //     setNextCursor(undefined);
    //     setHasMore(true);
    //     setLoading(false);
    //     resetEditorialSequence();
    // }, []);

    // Эффект для обработки изменения категорий
    useEffect(() => {
        // Проверяем, изменились ли категории
        const categoriesChanged = JSON.stringify(previousCategoriesRef.current) !== JSON.stringify(categories);

        if (categoriesChanged) {
            // Очищаем предыдущий таймер
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Сбрасываем состояние

            // Устанавливаем новый таймер для загрузки данных с задержкой
            debounceTimeoutRef.current = setTimeout(() => {
                if (!loading) {
                    setPrompts([]);
                    setNextCursor(undefined);
                    setHasMore(true);
                    const newBaseHash = Date.now().toString();
                    setHash(instanceId ? `${instanceId}_${newBaseHash}` : newBaseHash);
                    resetEditorialSequence();
                    setShouldReload(true); // Устанавливаем флаг для перезагрузки
                }
            }, 500); // Задержка 500ms
        }

        // Обновляем ссылку на предыдущие категории
        previousCategoriesRef.current = categories;

        // Cleanup функция для очистки таймера
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories]);

    // Эффект для перезагрузки данных после сброса состояния
    useEffect(() => {
        if (shouldReload && !loading) {
            setShouldReload(false);
            loadMore();
        }
    }, [shouldReload, loading, loadMore, nextCursor]);

    // Автоматическая загрузка данных при монтировании
    useEffect(() => {
        if (prompts.length === 0 && hasMore) {
            setShouldReload(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        prompts,
        loading,
        hasMore,
        loadMore,
        // reset,
    };
};
